// HQ → Branches & numbers: the organization's branches (from salon-admin) and the WhatsApp numbers customers
// reach them on. The main number stays in .env and serves every branch (chats there choose a branch first).
// A branch gets its own number from WhatsApp Accounts (account → number → Use for branch); here its numbers are
// edited, checked and tested.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api, { formatDateTime } from '../services/api';
import { useEngagementScope } from '../context/EngagementScope';
import { engagementPath } from '../engagementPaths';
import BranchSelect from '../components/BranchSelect';
import { Loader, Modal, PageHead, Spinner } from '../components/ui';

const STATUS_TONE = { active: 'ok', disabled: 'muted', error: 'danger' };
const EMPTY = { franchise_id: '', label: '', display_number: '', phone_number_id: '', business_account_id: '', access_token: '' };

function NumberForm({ initial, branches, onClose, onSaved }) {
  const editing = Boolean(initial?.id);
  const [form, setForm] = useState(
    editing
      ? { ...EMPTY, ...initial, phone_number_id: initial.phone_number_id || '', business_account_id: initial.business_account_id || '', access_token: '' }
      : EMPTY,
  );
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      let res;
      if (editing && initial.is_main) {
        res = await api.updateChannel(initial.id, { label: form.label, display_number: form.display_number });
      } else if (editing) {
        const patch = { label: form.label, display_number: form.display_number, phone_number_id: form.phone_number_id, business_account_id: form.business_account_id };
        if (form.access_token) patch.access_token = form.access_token;
        res = await api.updateChannel(initial.id, patch);
      } else {
        res = await api.addChannel({ ...form, franchise_id: Number(form.franchise_id) });
      }
      const check = res.check;
      if (check && !check.ok) toast.error(`Saved, but the check failed: ${check.message}`);
      else toast.success(check ? `Saved and checked: ${check.message}` : 'Saved');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const main = editing && initial.is_main;
  const ready = main ? form.label.trim() : form.label.trim() && form.phone_number_id.trim() && (editing || form.franchise_id);
  return (
    <Modal title={main ? 'Main number' : editing ? 'Edit branch number' : 'Add a branch WhatsApp number'} onClose={onClose} size="wide">
      {main ? (
        <div className="de-small de-muted" style={{ marginBottom: 12 }}>
          The main number’s id and token stay in the server’s .env. Only the name and the number shown to customers can be changed here.
        </div>
      ) : (
        <div className="de-callout" style={{ marginBottom: 14 }}>
          First add the number to the WhatsApp Business Account in Meta Business Manager and verify it. Then enter its phone number id here.
          It is checked with Meta before it goes live. Chats on this number are only for its branch.
        </div>
      )}
      <div className="de-form-grid de-form-grid--2">
        {!editing ? (
          <div className="de-field" style={{ gridColumn: '1 / -1' }}>
            <span className="de-label">Branch *</span>
            <BranchSelect
              branches={branches}
              value={form.franchise_id}
              onChange={(next) => setForm((current) => ({ ...current, franchise_id: next }))}
              placeholder="Choose the branch…"
              optionMeta={(branch) => ({
                disabled: Boolean(branch.own_number),
                suffix: branch.own_number ? ' (has a number)' : '',
              })}
            />
          </div>
        ) : null}
        <label className="de-field">
          <span className="de-label">Name *</span>
          <input className="de-input de-input--bordered" value={form.label} onChange={set('label')} placeholder="Naturals Anna Nagar" />
        </label>
        <label className="de-field">
          <span className="de-label">Number shown to customers</span>
          <input className="de-input de-input--bordered" value={form.display_number} onChange={set('display_number')} placeholder="+91 98xxx xxxxx" />
        </label>
        {!main ? (
          <>
            <label className="de-field">
              <span className="de-label">Phone number id *</span>
              <input className="de-input de-input--bordered de-mono" value={form.phone_number_id} onChange={set('phone_number_id')} placeholder="From Meta → WhatsApp → API setup" />
            </label>
            <label className="de-field">
              <span className="de-label">WhatsApp Business Account id</span>
              <input className="de-input de-input--bordered de-mono" value={form.business_account_id} onChange={set('business_account_id')} placeholder="Only if it is not the main account" />
            </label>
            <label className="de-field" style={{ gridColumn: '1 / -1' }}>
              <span className="de-label">Access token</span>
              <input
                className="de-input de-input--bordered de-mono"
                type="password"
                autoComplete="new-password"
                value={form.access_token}
                onChange={set('access_token')}
                placeholder={editing ? 'Leave empty to keep the saved token' : 'Only if it is not on the main account (uses the main token otherwise)'}
              />
              <span className="de-small de-muted">Stored encrypted. It is never shown again.</span>
            </label>
          </>
        ) : null}
      </div>
      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={!ready || saving} onClick={save}>
          {saving ? <Spinner light /> : null}
          {editing ? 'Save' : 'Add and check'}
        </button>
      </div>
    </Modal>
  );
}

function TestModal({ channel, onClose }) {
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.testChannel(channel.id, to);
      if (r.ok) toast.success('Test message sent');
      else toast.error(r.error || 'Not sent');
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Send a test from ${channel.label}`} onClose={onClose}>
      <label className="de-field">
        <span className="de-label">Send to (mobile)</span>
        <input className="de-input de-input--bordered" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Your own number" />
      </label>
      <div className="de-form-actions" style={{ marginTop: 16 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={to.replace(/\D/g, '').length < 10 || busy} onClick={send}>
          {busy ? <Spinner light /> : null}
          Send test
        </button>
      </div>
    </Modal>
  );
}

export default function Branches({ whatsappOnly = false }) {
  const { branches: visible, canManage } = useEngagementScope();
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState(visible);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(null);
  const [busy, setBusy] = useState('');

  const load = () =>
    Promise.all([api.listChannels(), api.branches()])
      .then(([c, b]) => {
        setData(c);
        setBranches(b);
      })
      .catch((e) => toast.error(e.message));

  useEffect(() => {
    load();
  }, []);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try {
      const r = await fn();
      if (r && r.ok === false) toast.error(r.message || 'Check failed');
      else toast.success(ok);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };

  if (!data) return <Loader />;
  const editable = canManage && data.can_edit;
  return (
    <div className="de-fade">
      <PageHead
        title="Branches & numbers"
        sub="The main number serves every branch (customers choose their branch in the chat). A branch can also have its own WhatsApp number."
        action={
          editable ? (
            <Link to={engagementPath('wa-accounts')} className="de-btn de-btn--primary">
              Add numbers from WhatsApp accounts → Use for branch
            </Link>
          ) : null
        }
      />
      {editable && !data.key_set ? (
        <div className="de-callout" style={{ marginBottom: 14 }}>
          Branch numbers need NATURALS_ENGAGEMENT_CHANNEL_KEY in the server’s .env (it encrypts their tokens). Ask the team to add it, then reload.
        </div>
      ) : null}

      <div className="de-section-label">Numbers</div>
      <div className="de-table-shell" style={{ marginBottom: 20 }}>
        <div className="de-table-wrap">
          <table className="de-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Number</th>
                <th>Serves</th>
                <th>Status</th>
                <th>Templates</th>
                <th>Last check</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.channels.filter((c) => !whatsappOnly || c.channel === 'whatsapp').map((c) => (
                <tr key={c.id}>
                  <td className="de-td-strong">
                    {c.label}
                    {c.is_main ? <span className="de-pill de-pill--info de-pill--sm" style={{ marginLeft: 6 }}>Main</span> : null}
                    <div className="de-small de-muted">{c.channel === 'instagram' ? 'Instagram' : 'WhatsApp'}</div>
                  </td>
                  <td className="de-td-mono">{c.display_number || '—'}</td>
                  <td>{c.branch_name}</td>
                  <td>
                    <span className={`de-pill de-pill--sm de-pill--${STATUS_TONE[c.status] || 'muted'}`}>{c.status}</span>
                  </td>
                  <td>{c.is_main ? '—' : c.templates_ok ? <span className="de-ok-text">Approved</span> : <span className="de-danger-text">Not yet</span>}</td>
                  <td className="de-small de-muted" style={{ maxWidth: 260 }}>
                    {c.last_check_at ? formatDateTime(c.last_check_at) : '—'}
                    {c.last_check_result ? <div title={c.last_check_result}>{c.last_check_result.slice(0, 90)}</div> : null}
                  </td>
                  <td className="de-td-right" style={{ whiteSpace: 'nowrap' }}>
                    {editable && c.channel === 'whatsapp' ? (
                      <div className="de-row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setEditing(c)}>
                          Edit
                        </button>
                        <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={busy === `check${c.id}`} onClick={() => act(`check${c.id}`, () => api.checkChannel(c.id), 'Checked')}>
                          Check
                        </button>
                        <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setTesting(c)}>
                          Test
                        </button>
                        {!c.is_main && !c.templates_ok ? (
                          <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={busy === `tpl${c.id}`} onClick={() => act(`tpl${c.id}`, () => api.setupChannelTemplates(c.id), 'Templates submitted for approval')}>
                            Submit templates
                          </button>
                        ) : null}
                        {!c.is_main ? (
                          <button
                            type="button"
                            className="de-btn de-btn--outline de-btn--sm"
                            disabled={busy === `toggle${c.id}`}
                            onClick={() => act(`toggle${c.id}`, () => api.updateChannel(c.id, { active: c.status === 'disabled' }), c.status === 'disabled' ? 'Turned on' : 'Turned off')}
                          >
                            {c.status === 'disabled' ? 'Turn on' : 'Turn off'}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="de-section-label">Branches</div>
      <div className="de-table-shell">
        <div className="de-table-wrap">
          <table className="de-table">
            <thead>
              <tr>
                <th>Branch</th>
                <th>Address</th>
                <th>Phone</th>
                <th>Own WhatsApp number</th>
              </tr>
            </thead>
            <tbody>
              {branches.map((b) => (
                <tr key={b.franchise_id}>
                  <td className="de-td-strong">{b.label || b.name}</td>
                  <td className="de-small">{[b.address, b.city].filter(Boolean).join(', ') || '—'}</td>
                  <td className="de-td-mono">{b.phone || '—'}</td>
                  <td>{b.own_number || <span className="de-muted">Main number</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="de-small de-muted de-mt-8">Branches, addresses and service lists are managed in the Naturals workspace; staff alert numbers per branch are on Human intervention.</div>

      {editing ? (
        <NumberForm
          initial={editing.id ? editing : null}
          branches={branches}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      ) : null}
      {testing ? <TestModal channel={testing} onClose={() => setTesting(null)} /> : null}
    </div>
  );
}
